import React, { useState } from 'react';
import {
  Search, Plus, Edit, Trash2, Eye, CheckCircle, XCircle,
  Truck, Package, DollarSign, MoreVertical
} from 'lucide-react';

const couriersData = [
  {
    id: '1',
    name: 'FastShip Jamaica',
    prefix: 'FS',
    contactName: 'Marcus Johnson',
    email: 'marcus@fastship.jm',
    phone: '+1 876-555-0123',
    status: 'active',
    totalBags: 145,
    totalSingles: 432,
    totalInvoiced: 485420,
    balance: 12500
  },
  {
    id: '2',
    name: 'SpeedyCourier',
    prefix: 'SC',
    contactName: 'Sarah Williams',
    email: 'sarah@speedycourier.jm',
    phone: '+1 876-555-0456',
    status: 'active',
    totalBags: 98,
    totalSingles: 287,
    totalInvoiced: 342150,
    balance: 0
  },
  {
    id: '3',
    name: 'IslandExpress',
    prefix: 'IE',
    contactName: 'David Chen',
    email: 'david@islandexpress.jm',
    phone: '+1 876-555-0789',
    status: 'active',
    totalBags: 72,
    totalSingles: 189,
    totalInvoiced: 238029,
    balance: 38029
  },
  {
    id: '4',
    name: 'KP Direct',
    prefix: 'KP',
    contactName: 'Internal',
    email: 'retail@kplogistics.jm',
    phone: '+1 876-555-0000',
    status: 'active',
    totalBags: 0,
    totalSingles: 856,
    totalInvoiced: 428800,
    balance: 0
  },
  {
    id: '5',
    name: 'QuickShip',
    prefix: 'QS',
    contactName: 'Lisa Brown',
    email: 'lisa@quickship.jm',
    phone: '+1 876-555-1111',
    status: 'inactive',
    totalBags: 12,
    totalSingles: 45,
    totalInvoiced: 32500,
    balance: 5200
  },
];

const prefixColors = {
  FS: '#3b82f6',
  SC: '#06b6d4',
  IE: '#84cc16',
  KP: '#f59e0b',
  QS: '#a855f7'
};

export default function Couriers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredCouriers = couriersData.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.prefix.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const activeCouriers = couriersData.filter(c => c.status === 'active').length;
  const totalOutstanding = couriersData.reduce((sum, c) => sum + c.balance, 0);

  return (
    <div className="couriers-page" data-testid="couriers-page">
      {/* Summary */}
      <div className="courier-summary-grid">
        <div className="summary-card">
          <Truck size={24} />
          <div className="summary-content">
            <span className="summary-value">{activeCouriers}</span>
            <span className="summary-label">Active Couriers</span>
          </div>
        </div>
        <div className="summary-card">
          <Package size={24} />
          <div className="summary-content">
            <span className="summary-value">{couriersData.reduce((s, c) => s + c.totalBags + c.totalSingles, 0).toLocaleString()}</span>
            <span className="summary-label">Total Items Processed</span>
          </div>
        </div>
        <div className="summary-card">
          <DollarSign size={24} />
          <div className="summary-content">
            <span className="summary-value">${totalOutstanding.toLocaleString()}</span>
            <span className="summary-label">Outstanding Balance</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search couriers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="courier-search"
          />
        </div>
        <div className="toolbar-right">
          <select 
            className="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="courier-status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
            data-testid="add-courier-btn"
          >
            <Plus size={16} />
            Add Courier
          </button>
        </div>
      </div>

      {/* Couriers Grid */}
      <div className="couriers-grid">
        {filteredCouriers.map((courier) => (
          <div 
            key={courier.id} 
            className={`courier-card ${courier.status}`}
            data-testid={`courier-card-${courier.id}`}
          >
            <div className="courier-header">
              <div 
                className="courier-prefix-badge"
                style={{ backgroundColor: prefixColors[courier.prefix] }}
              >
                {courier.prefix}
              </div>
              <span className={`status-indicator ${courier.status}`}>
                {courier.status === 'active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                {courier.status}
              </span>
            </div>

            <div className="courier-body">
              <h3 className="courier-name">{courier.name}</h3>
              <div className="courier-contact">
                <span>{courier.contactName}</span>
                <span>{courier.email}</span>
                <span>{courier.phone}</span>
              </div>

              <div className="courier-stats">
                <div className="stat-item">
                  <span className="stat-label">Bags</span>
                  <span className="stat-value">{courier.totalBags}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Singles</span>
                  <span className="stat-value">{courier.totalSingles}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Invoiced</span>
                  <span className="stat-value">${(courier.totalInvoiced / 1000).toFixed(0)}k</span>
                </div>
              </div>

              {courier.balance > 0 && (
                <div className="balance-warning">
                  Outstanding: ${courier.balance.toLocaleString()}
                </div>
              )}
            </div>

            <div className="courier-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedCourier(courier)}
                data-testid={`view-${courier.id}`}
              >
                <Eye size={14} /> View Details
              </button>
              <button className="btn btn-ghost" data-testid={`edit-${courier.id}`}>
                <Edit size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Courier Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="add-courier-modal">
            <div className="modal-header">
              <h3>Add New Courier</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Courier Name *</label>
                  <input type="text" placeholder="e.g., FastShip Jamaica" data-testid="courier-name-input" />
                </div>
                <div className="form-group small">
                  <label>Prefix *</label>
                  <input type="text" placeholder="e.g., FS" maxLength={3} data-testid="courier-prefix-input" />
                </div>
              </div>
              <div className="form-group">
                <label>Contact Name *</label>
                <input type="text" placeholder="Primary contact person" data-testid="contact-name-input" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" placeholder="contact@courier.com" data-testid="email-input" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" placeholder="+1 876-XXX-XXXX" data-testid="phone-input" />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea placeholder="Additional notes about this courier..." rows={3} data-testid="notes-input"></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" data-testid="save-courier-btn">
                <Plus size={14} /> Add Courier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Courier Detail Modal */}
      {selectedCourier && (
        <div className="modal-overlay" onClick={() => setSelectedCourier(null)}>
          <div className="modal-content courier-detail-modal" onClick={e => e.stopPropagation()} data-testid="courier-detail-modal">
            <div className="modal-header">
              <div className="header-with-badge">
                <div 
                  className="courier-prefix-badge large"
                  style={{ backgroundColor: prefixColors[selectedCourier.prefix] }}
                >
                  {selectedCourier.prefix}
                </div>
                <div>
                  <h3>{selectedCourier.name}</h3>
                  <span className={`status-badge ${selectedCourier.status}`}>
                    {selectedCourier.status}
                  </span>
                </div>
              </div>
              <button className="close-btn" onClick={() => setSelectedCourier(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h4>Contact Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Contact Name</span>
                    <span className="value">{selectedCourier.contactName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email</span>
                    <span className="value">{selectedCourier.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone</span>
                    <span className="value">{selectedCourier.phone}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Statistics</h4>
                <div className="stats-grid">
                  <div className="stat-box">
                    <span className="stat-number">{selectedCourier.totalBags}</span>
                    <span className="stat-label">Total Bags</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{selectedCourier.totalSingles}</span>
                    <span className="stat-label">Total Singles</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">${selectedCourier.totalInvoiced.toLocaleString()}</span>
                    <span className="stat-label">Total Invoiced</span>
                  </div>
                  <div className="stat-box balance">
                    <span className="stat-number">${selectedCourier.balance.toLocaleString()}</span>
                    <span className="stat-label">Outstanding Balance</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Recent Invoices</h4>
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>INV-2026-4521</td>
                      <td>2026-02-28</td>
                      <td>$85,420</td>
                      <td><span className="status-badge sent">Sent</span></td>
                    </tr>
                    <tr>
                      <td>INV-2026-4498</td>
                      <td>2026-02-25</td>
                      <td>$72,340</td>
                      <td><span className="status-badge paid">Paid</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedCourier(null)}>Close</button>
              <button className="btn btn-secondary">
                <Edit size={14} /> Edit Courier
              </button>
              <button className="btn btn-primary">View All Invoices</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
